import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { AuthContext } from "../context/AuthContext";
import { formatStatus, getStatusBadgeClass, truncateText } from "../utils/Utils";
import { EyeIcon } from "@heroicons/react/24/outline";
import { usePagedList } from "../hooks/usePagedList";
import {
  EmptyState,
  Page,
  PageHeader,
  PageLoader,
  Pagination,
  Panel,
  SearchInput,
  TableShell,
  Td,
  Th,
  Tr,
} from "../components/ui";

const MyTicket = () => {
  const { user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await axiosInstance.get(`/users/${user.id}/tickets`);
        setTickets(response.data);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [user.id]);

  const filteredTickets = tickets.filter(
    (ticket) =>
      ticket.ticket_id
        .toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paged = usePagedList(filteredTickets, 25, searchTerm);

  if (loading) {
    return (
      <Page>
        <PageHeader title="My Tickets" onBack={() => window.history.back()} />
        <PageLoader label="Loading your tickets…" />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title="My Tickets"
        subtitle={`${tickets.length} ticket${tickets.length === 1 ? "" : "s"}`}
        onBack={() => window.history.back()}
      />

      <SearchInput
        placeholder="Search by ticket id, subject…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {filteredTickets.length === 0 ? (
        <EmptyState
          title="No tickets found"
          hint={
            tickets.length === 0
              ? "Open a ticket from a file when you need a hand."
              : "No ticket matches your search."
          }
        />
      ) : (
        <Panel padded={false}>
          <TableShell className="rounded-[22px] border-0">
            <thead>
              <tr>
                <Th>Ticket ID</Th>
                <Th>Subject</Th>
                <Th>Status</Th>
                <Th>Opening Date</Th>
                <Th>Closing Date</Th>
                <Th align="center">View</Th>
              </tr>
            </thead>
            <tbody>
              {paged.visible.map((ticket) => (
                <Tr key={ticket.ticket_id}>
                  <Td className="font-mono text-xs">{ticket.ticket_id}</Td>
                  <Td className="font-medium text-gray-800 dark:text-slate-200">
                    {truncateText(ticket.subject, 35)}
                  </Td>
                  <Td>
                    <span className={getStatusBadgeClass(ticket.status)}>
                      {formatStatus(ticket.status)}
                    </span>
                  </Td>
                  <Td className="whitespace-nowrap text-xs">{ticket.opening_date}</Td>
                  <Td className="whitespace-nowrap text-xs">{ticket.closing_date || "N/A"}</Td>
                  <Td className="text-center">
                    <Link
                      to={`/view-file/${ticket.file_id}`}
                      title="View ticket"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        </Panel>
      )}

      <Pagination
        page={paged.page}
        totalPages={paged.totalPages}
        onChange={paged.setPage}
        total={paged.total}
        pageSize={paged.pageSize}
      />
    </Page>
  );
};

export default MyTicket;
